import { AppBar, Box, LinearProgress, Stack, SxProps, Toolbar, Typography } from '@mui/material';
import { BaseTheme, Button, IconButton, squareOnStickyHeaderStyle, stickyTopBarProps } from 'design-system';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { MdArrowBack, MdArrowBackIos, MdSearch } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router';
import { getRoutePrefix } from 'services/theming';
import { useFeatures } from 'hooks/useFeatures';
import useIsDarkMode from 'hooks/useIsDarkMode';
import { useIsPrint } from 'hooks/useIsPrint';
import useIsViewport from 'hooks/useIsViewport';
import { getNavigationTarget } from 'utils/getNavigationTarget';
import { isHeadless } from 'utils/headless';
import { isIOS } from 'utils/is-ios';
import HelpActions from './HelpActions';
import ProfileConfigButton from './ProfileConfig/ProfileConfigButton';
import ProfileSwitch from './ProfileSwitch';
import SearchBar, { SearchbarProps } from './SearchBar';

interface MobileHeaderProps {
  title?: ReactNode;
  sx?: SxProps<BaseTheme>;
  children?: ReactNode;
  actions?: ReactNode;
  afterLeftNode?: ReactNode;
  showSearch?: boolean;
  hasBackButton?: boolean;
  className?: string;
  returnPath?: string;
  overrideBackButton?: () => void;
  childrenPadding?: boolean;
  childrenRoundedBorder?: boolean;
  alwaysShowSearch?: boolean;
  searchProps?: SearchbarProps;
  noMargin?: boolean;
  isActionsFilling?: boolean;
  isLoading?: boolean;
  hasBackLabel?: boolean;
  loadingLabel?: string;
}

const MobileHeader = ({
  title,
  sx,
  children,
  afterLeftNode,
  showSearch = true,
  className = '',
  actions,
  hasBackButton = true,
  returnPath,
  overrideBackButton,
  childrenPadding = true,
  childrenRoundedBorder = true,
  alwaysShowSearch = false,
  searchProps = undefined,
  noMargin = false,
  isActionsFilling = false,
  isLoading = false,
  hasBackLabel = true,
  loadingLabel = 'Loading',
}: MobileHeaderProps) => {
  const MOBILE_HEADER_DEFAULT_HEIGHT = 48;

  const isPrint = useIsPrint();
  const isRobot = isHeadless();
  const isDarkMode = useIsDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const prefix = getRoutePrefix();
  const [showSearchInput, setShowSearchInput] = useState(alwaysShowSearch);
  const isSearchable = !!searchProps?.onChange && showSearch;
  const isIOSDevice = isIOS();
  const headerRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(MOBILE_HEADER_DEFAULT_HEIGHT);
  const isMobile = useIsViewport('md');
  const isNavigationEnabled = useFeatures().isEnabled('Navigation');

  useLayoutEffect(() => {
    if (!isMobile || isRobot || isPrint) {
      return;
    }

    const el = headerRef.current;
    if (!el) {
      return;
    }

    const updateHeight = () => {
      const height = el.offsetHeight;
      const spacerHeight = height + (noMargin ? 0 : 8);

      if (spacerRef.current) {
        spacerRef.current.style.minHeight = `${height}px`;
        spacerRef.current.style.height = `${spacerHeight}px`;
      }

      setHeaderHeight(height);
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [isMobile, isRobot, isPrint, showSearchInput, noMargin]);

  const handleBack = () => {
    if (overrideBackButton) {
      overrideBackButton();
      return;
    }

    // On mobile, prefer browser back when we have history - avoids wrong returnPath from reloads/state loss
    if (isMobile && window.history.length > 2) {
      navigate(-1);
      return;
    }

    if (returnPath) {
      const path = isNavigationEnabled ? getNavigationTarget(returnPath).path : returnPath;
      navigate(path, { replace: true });
      return;
    }

    const stateReturnPath = (location.state as { returnPath?: string })?.returnPath;
    if (stateReturnPath) {
      const path = isNavigationEnabled ? getNavigationTarget(stateReturnPath).path : stateReturnPath;
      navigate(path, { replace: true });
      return;
    }

    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(prefix || '/');
    }
  };

  if (isRobot || isPrint) return null;

  const handleSetInputFalse = () => {
    if (!alwaysShowSearch) setShowSearchInput(false);
  };

  if (!isMobile) {
    return null;
  }
  return (
    <>
      <Box
        {...stickyTopBarProps}
        ref={headerRef}
        className={className?.trim()}
        sx={(theme) => ({
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 100,
          width: '100vw',
          marginBottom: noMargin ? 0 : theme.spacing(1),
          [theme.breakpoints.up('md')]: {
            display: 'none',
          },
        })}
      >
        <Stack
          direction="row"
          className={className?.trim()}
          sx={{
            textAlign: 'left',
            width: '100%',
            borderBottom: !!children || isDarkMode ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
            ...{ sx },
          }}
        >
          <AppBar
            enableColorOnDark
            position="static"
            sx={[
              (theme) => ({
                backgroundColor: isDarkMode ? theme.palette.primary.main : theme.palette.common.white,
                paddingBottom: children && childrenPadding ? theme.spacing(1) : 0,
                borderRadius: children && childrenRoundedBorder ? theme.spacing(0, 0, 1.5, 1.5) : 0,
              }),
              squareOnStickyHeaderStyle,
            ]}
          >
            <Toolbar
              sx={[
                (theme) => ({
                  gap: 2,
                  color: isDarkMode ? theme.palette.common.white : theme.palette.primary.main,
                  backgroundColor: isDarkMode ? theme.palette.primary.main : theme.palette.common.white,
                  paddingRight: theme.spacing(1),
                  borderRadius: children && childrenRoundedBorder ? theme.spacing(0, 0, 1.5, 1.5) : 0,
                }),
                squareOnStickyHeaderStyle,
              ]}
            >
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: theme.spacing(6),
                  width: '100%',
                })}
              >
                {showSearchInput ? (
                  <Stack
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {alwaysShowSearch && <ProfileSwitch isButton />}
                    {afterLeftNode}
                    {searchProps && (
                      <SearchBar
                        {...searchProps}
                        sx={{ flexGrow: 1 }}
                        value={searchProps?.value ?? ''}
                        onChange={(text) => {
                          if (text.length === 0) handleSetInputFalse();
                          if (searchProps?.onChange) searchProps.onChange(text);
                        }}
                        onClose={() => handleSetInputFalse}
                        chips={{
                          data: searchProps.chips?.data ?? [],
                          onClose: () => {
                            handleSetInputFalse();
                            searchProps.chips?.onClose && searchProps.chips.onClose();
                          },
                        }}
                        showClear={!alwaysShowSearch}
                      />
                    )}
                  </Stack>
                ) : (
                  <>
                    {title && !hasBackButton ? (
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                        <ProfileSwitch isButton />
                        {afterLeftNode}
                        <Box sx={{ textAlign: 'center' }}>{title}</Box>
                      </Box>
                    ) : (
                      <>
                        <Button
                          sx={(theme) => ({
                            color: 'inherit',
                            background: 'transparent',
                            display: 'flex',
                            border: 'none',
                            alignItems: 'center',
                            height: '100%',
                            padding: theme.spacing(0.5, 2, 0.5, 0),
                            cursor: 'pointer',
                            gap: theme.spacing(0.25),
                            minWidth: isActionsFilling || !hasBackLabel ? 'auto' : 72,
                          })}
                          onClick={handleBack}
                          aria-label="Back"
                        >
                          {isIOSDevice ? <MdArrowBackIos size={20} /> : <MdArrowBack size={20} />}
                          {!isActionsFilling && hasBackLabel && <Typography variant="body1">Back</Typography>}
                        </Button>
                        {afterLeftNode}
                        {title && <Box sx={{ textAlign: 'center' }}>{title}</Box>}
                      </>
                    )}

                    <Stack
                      direction="row"
                      sx={[
                        {
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        },
                        (theme) => ({
                          minWidth: 72,
                          gap: theme.spacing(1),
                        }),
                        isActionsFilling && { flex: 1, minWidth: 0 },
                      ]}
                    >
                      {actions}
                      <ProfileConfigButton icon />
                      {isSearchable && (
                        <IconButton
                          size="small"
                          aria-label="Search"
                          color="inherit"
                          onClick={() => setShowSearchInput(true)}
                        >
                          <MdSearch size={24} />
                        </IconButton>
                      )}
                      <HelpActions />
                    </Stack>
                  </>
                )}
              </Box>
            </Toolbar>
            {children}
          </AppBar>
        </Stack>
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3 }} aria-live="polite">
          {isLoading && (
            <LinearProgress aria-label={loadingLabel} color="secondary" sx={{ backgroundColor: 'transparent' }} />
          )}
        </Box>
      </Box>
      <Box
        ref={spacerRef}
        sx={{
          minHeight: headerHeight,
          height: headerHeight + (noMargin ? 0 : 8),
        }}
      />
    </>
  );
};

export default MobileHeader;
